export const PDFTRON_SUPPORTED_EXTENSIONS = [
    'pdf',
    'doc',
    'docx',
    'ppt',
    'pptx',
    'xlsx',
    'xls',
    'csv',
    'png',
    'jpg',
    'jpeg',
    'gif',
    'bmp',
    'tiff',
    'tif',
    'txt',
    'rtf',
    'md',
    'markdown',
];

/** Preview in-app without WebViewer (fetch + render). HTML only — txt uses WebViewer Server. */
export const NATIVE_TEXT_PREVIEW_EXTENSIONS = ['html', 'htm'];

/** Office formats viewed as PDF via WebViewer Server (view-only, no Office Editor license needed). */
export const OFFICE_SERVER_PDF_EXTENSIONS = ['doc', 'docx', 'ppt', 'pptx'];

export const getFileExtensionFromUrl = filenameOrUrl => {
    if (!filenameOrUrl) return null;
    const path = String(filenameOrUrl).split('?')[0];
    const parts = path.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : null;
};

/** Prefer display name, then blob URL, then explicit extension field. */
export const getFileExtensionFromFile = file => {
    if (!file) return null;
    return (
        getFileExtensionFromUrl(file.document_name) ||
        getFileExtensionFromUrl(file.original_filename) ||
        getFileExtensionFromUrl(file.document) ||
        getFileExtensionFromUrl(file.file) ||
        (file.extension ? String(file.extension).toLowerCase() : null)
    );
};

/**
 * Extension passed to WebViewer load options.
 * Markdown is plain text; WebViewer Server supports txt, not md natively.
 */
export const getWebViewerLoadExtension = extension => {
    const ext = String(extension || '').toLowerCase();
    if (ext === 'md' || ext === 'markdown') return 'txt';
    return ext || 'pdf';
};

export const isDocxExtension = extension => {
    return String(extension || '').toLowerCase() === 'docx';
};

export const isDocxFile = file => isDocxExtension(getFileExtensionFromFile(file));

export const isNativeTextPreviewExtension = extension => {
    return NATIVE_TEXT_PREVIEW_EXTENSIONS.includes(String(extension || '').toLowerCase());
};

export const isNativeTextPreviewFile = file => isNativeTextPreviewExtension(getFileExtensionFromFile(file));

/** @returns {'html' | 'plain'} */
export const getNativeTextPreviewMode = extension => {
    const ext = String(extension || '').toLowerCase();
    return ext === 'html' || ext === 'htm' ? 'html' : 'plain';
};

export const canPreviewFile = file => isPDFTronSupportedFile(file) || isNativeTextPreviewFile(file);

/**
 * How WebViewer should open this file (all via WebViewer Server / PDF-style view).
 * - pdf: PDF via WebViewer Server
 * - office-pdf: .doc / .docx / .ppt / .pptx via server conversion
 * - server: spreadsheets, images, text, etc. via server conversion
 */
export const getWebViewerPreviewKind = extension => {
    const ext = String(extension || '').toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (OFFICE_SERVER_PDF_EXTENSIONS.includes(ext)) return 'office-pdf';
    return 'server';
};

/** Citation search/highlight only works on paginated PDF-style views. */
export const supportsPdfCitationHighlight = extension => {
    const kind = getWebViewerPreviewKind(extension);
    return kind === 'pdf' || kind === 'office-pdf' || kind === 'server';
};

/** WebViewer Server fetches the blob (not the user's browser). */
export const usesWebViewerServerForBlobFetch = () => true;

/**
 * WebViewer.Iframe constructor options.
 * All preview types use WebViewer Server so Azure blob access stays on the VNet /
 * allowlisted App Service IPs (pdftron-lens-server).
 */
export const getWebViewerIframeOptions = ({ fileUrl, extension, filename }) => {
    const options = {
        path: '/lib/webviewer',
        initialDoc: fileUrl,
        extension: getWebViewerLoadExtension(extension),
        licenseKey: process.env.REACT_APP_PDF_EXPRESS_KEY,
        webviewerServerURL: process.env.REACT_APP_PDF_WEBVIEWER_URL,
    };
    if (filename) {
        options.filename = filename;
    }
    return options;
};

export const isPDFTronSupportedExtension = extension => {
    if (!extension) return false;
    return PDFTRON_SUPPORTED_EXTENSIONS.includes(String(extension).toLowerCase());
};

export const isPDFTronSupportedFilename = filenameOrUrl => {
    return isPDFTronSupportedExtension(getFileExtensionFromUrl(filenameOrUrl));
};

export const isPDFTronSupportedFile = file => {
    if (!file) return false;
    if (typeof file.is_pdftron_supported === 'boolean') {
        return file.is_pdftron_supported;
    }
    return isPDFTronSupportedExtension(getFileExtensionFromFile(file));
};

/** @param {{ original_filename?: string, file?: string, extension?: string, is_pdftron_supported?: boolean }} attachment */
export const isPDFTronSupportedAttachment = attachment => isPDFTronSupportedFile(attachment);

/** Shape attachment as a minimal file object for PDFTron loaders expecting `document`. */
export const attachmentToViewerFile = attachment => ({
    id: attachment?.id,
    document: attachment?.file,
    document_name: attachment?.original_filename,
    extension: attachment?.extension || getFileExtensionFromUrl(attachment?.original_filename || attachment?.file),
});
